<template>
  <div class="md-layout">
    <md-card>
      <!-- Toolbar -->
      <md-card-content>
        <div class="d-flex justify-content-between mb-2">
          <div>
            <slot name="toolbar-left"></slot>
          </div>
          <div class="d-flex">
            <md-field>
              <md-input
                  v-model="query"
                  type="search"
                  placeholder="Search..."
                  clearable
                  @input="debouncedGetList"
              />
            </md-field>

            <md-field class="ml-2">
              <md-select v-model="pagination.perPage" name="perPage">
                <md-option v-for="opt in perPageOptions" :key="opt" :value="opt">
                  {{ opt }}
                </md-option>
              </md-select>
            </md-field>
          </div>
        </div>

        <!-- Table -->
        <md-table
            :value="table"
            :md-sort.sync="sortation.field"
            :md-sort-order.sync="sortation.order"
            :md-sort-fn="disableSort"
        >
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell
                v-for="col in columns"
                :key="col.field"
                @click="col.sortable !== false && setSort(col.field)"
                :class="{ 'active-header': sortation.field === col.field }"
                style="cursor: pointer;"
            >
              <!-- Custom cell slot -->
              <slot :name="'cell-' + col.field" :item="item">
                {{ formatCell(item, col) }}
              </slot>

              <span v-if="sortation.field === col.field">
                {{ sortation.order === 'asc' ? '↑' : '↓' }}
              </span>
            </md-table-cell>

            <!-- Actions -->
            <md-table-cell md-label="Actions">
              <slot name="actions" :item="item"></slot>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-card-content>

      <!-- Pagination -->
      <md-card-actions md-alignment="space-between">
        <div>
          Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
        </div>
        <pagination
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
        />
      </md-card-actions>
    </md-card>
  </div>
</template>

<script>
import _ from 'lodash';
import Pagination from '@/components/Pagination';

export default {
  components: { pagination: Pagination },

  props: {
    columns: { type: Array, required: true }, // { field, label, type?, sortable? }
    fetchData: { type: Function, required: true }, // async function returning { data, total }
    perPageOptions: { type: Array, default: () => [5, 10, 25, 50] },
  },

  data() {
    return {
      table: [],
      query: '',
      sortation: { field: this.columns[0]?.field || 'id', order: 'asc' },
      pagination: { perPage: 10, currentPage: 1 },
      total: 0,
    };
  },

  computed: {
    from() { return this.pagination.perPage * (this.pagination.currentPage - 1); },
    to() { return Math.min(this.from + this.pagination.perPage, this.total); },
  },

  watch: {
    sortation: { handler: 'getList', deep: true },
    pagination: { handler: 'getList', deep: true },
  },

  created() {
    this.debouncedGetList = _.debounce(this.getList, 300);
    this.getList();
  },

  methods: {
    disableSort() { return false; },

    async getList() {
      try {
        const params = {
          ...(this.sortation ? { sort: this.sortation.order === 'desc' ? `-${this.sortation.field}` : this.sortation.field } : {}),
          filter: this.query ? { search: this.query } : {},
          page: { number: this.pagination.currentPage, size: this.pagination.perPage },
        };
        const { data, total } = await this.fetchData(params);
        this.table = data;
        this.total = total;
      } catch (e) {
        console.error('Error fetching table data', e);
      }
    },

    setSort(field) {
      if (this.sortation.field === field) {
        this.sortation.order = this.sortation.order === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortation.field = field;
        this.sortation.order = 'asc';
      }
      this.getList();
    },

    formatCell(item, col) {
      if (col.type === 'boolean') return item[col.field] ? 'Yes' : 'No';
      if (col.type === 'date') return new Date(item[col.field]).toLocaleString();
      return item[col.field];
    },
  },
};
</script>

<style scoped>
.active-header { font-weight: bold; color: #3f51b5; }
.d-flex { display: flex; align-items: center; }
.ml-2 { margin-left: 0.5rem; }
.mb-2 { margin-bottom: 0.5rem; }
</style>